// Finickly — sample data for screens still on preview (P2 forecast / cashflow,
// P5 licenses, P2 alerts). Live screens read from the API via lib/api/.
// Amounts here are in cents/kopecks (integer math). UAH = ₴, USD = $.

export type Ccy = 'UAH' | 'USD'

export type ForecastWeek = {
  week: string
  label: string
  open: number
  income: number
  expense: number
  status: 'ok' | 'gap'
}

export type LicenseTimelineItem = {
  id: string
  date: string
  kind: 'inflow' | 'outflow'
  label: string
  amount: number
  txId: string
}

export type License = {
  id: string
  product: string
  customer: string
  status: 'active' | 'closed' | 'overdue'
  signed: string
  dueDate: string
  daysUntilDue: number
  received: number
  paid: number
  open: number
  ccy: Ccy
  timeline: LicenseTimelineItem[]
}

export type Alert = {
  id: string
  severity: 'danger' | 'warning' | 'info'
  title: string
  body: string
  action: string | null
}

// ---------- Formatters (cents-based, used by preview screens) ----------
const symbol = (ccy: Ccy) => (ccy === 'USD' ? '$' : '₴')

export const fmtAbs = (n: number, ccy: Ccy): string => {
  const abs = Math.abs(n)
  const whole = Math.floor(abs / 100)
  const frac = (abs % 100).toString().padStart(2, '0')
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return `${symbol(ccy)} ${grouped}.${frac}`
}

// ---------- Forecast (preview) ----------
export const FORECAST: ForecastWeek[] = [
  { week: 'W22', label: '26 трав → 1 черв', open: 47820000, income: 8240000, expense: -6680000, status: 'ok' },
  { week: 'W23', label: '2 → 8 черв', open: 49380000, income: 5120000, expense: -8900000, status: 'ok' },
  { week: 'W24', label: '9 → 15 черв', open: 45600000, income: 1200000, expense: -7200000, status: 'gap' },
  { week: 'W25', label: '16 → 22 черв', open: 39600000, income: 9840000, expense: -9200000, status: 'ok' },
  { week: 'W26', label: '23 → 29 черв', open: 40240000, income: 2400000, expense: -6800000, status: 'ok' },
  { week: 'W27', label: '30 черв → 6 лип', open: 35840000, income: 12480000, expense: -9400000, status: 'ok' },
  { week: 'W28', label: '7 → 13 лип', open: 38920000, income: 1800000, expense: -8200000, status: 'gap' },
  { week: 'W29', label: '14 → 20 лип', open: 32520000, income: 9600000, expense: -7400000, status: 'ok' },
  { week: 'W30', label: '21 → 27 лип', open: 34720000, income: 4200000, expense: -8800000, status: 'ok' },
  { week: 'W31', label: '28 лип → 3 серп', open: 30120000, income: 11800000, expense: -8600000, status: 'ok' },
  { week: 'W32', label: '4 → 10 серп', open: 33320000, income: 2200000, expense: -9100000, status: 'ok' },
  { week: 'W33', label: '11 → 17 серп', open: 26420000, income: 14400000, expense: -7800000, status: 'ok' },
]

// ---------- License (preview) ----------
export const LICENSE: License = {
  id: 'LIC-2026-014',
  product: 'Finickly Enterprise',
  customer: 'Wayne Enterprises',
  status: 'active',
  signed: '2025-08-14',
  dueDate: '2026-05-14',
  daysUntilDue: -1,
  received: 18720000,
  paid: 16200000,
  open: 2520000,
  ccy: 'USD',
  timeline: [
    { id: 'lt-1', date: '2025-08-14', kind: 'inflow', label: 'Початковий платіж', amount: 4800000, txId: 'tx-old-1' },
    { id: 'lt-2', date: '2025-11-14', kind: 'inflow', label: 'Q1 — внесок', amount: 4800000, txId: 'tx-old-2' },
    { id: 'lt-3', date: '2026-02-14', kind: 'inflow', label: 'Q2 — внесок', amount: 4800000, txId: 'tx-old-3' },
    { id: 'lt-4', date: '2026-05-14', kind: 'inflow', label: 'Q3 — внесок (очікується)', amount: 4320000, txId: 'tx-010' },
    { id: 'lt-5', date: '2025-09-01', kind: 'outflow', label: 'Впровадження — підрядник', amount: -2400000, txId: 'tx-old-4' },
    { id: 'lt-6', date: '2026-01-15', kind: 'outflow', label: 'Бонус за онбординг', amount: -1200000, txId: 'tx-old-5' },
  ],
}

// ---------- Alerts (preview, P2) ----------
export const ALERTS: Alert[] = [
  { id: 'al-1', severity: 'danger', title: 'Касовий розрив у прогнозі', body: 'Тиждень 9 черв — баланс падає до −$ 4 200. Перегляньте зобов’язання.', action: 'forecast' },
  { id: 'al-2', severity: 'warning', title: '3 рахунки до сплати цього тижня', body: 'Wayne, Globex, Stark — разом $ 21 200.', action: 'transactions' },
  { id: 'al-3', severity: 'warning', title: '1 транзакція прострочена', body: 'Initech — Спринт #14 — прострочення 6 днів.', action: 'transactions' },
  { id: 'al-4', severity: 'info', title: 'Курс Wise оновлено', body: 'UAH/USD = 39,51 (було 39,48).', action: null },
]

// ---------- Translations ----------
export const T = {
  nav: {
    dashboard: 'Огляд',
    accounts: 'Рахунки',
    transactions: 'Операції',
    forecast: 'Прогноз',
    cashflow: 'Cash-flow',
    salaries: 'Зарплати',
    licenses: 'Ліцензії',
    settings: 'Налаштування',
    operations: 'Операційне',
    reports: 'Звіти',
    catalogs: 'Довідники',
  },
  buttons: {
    new_tx: 'Нова операція',
    new_license: 'Нова ліцензія',
    save: 'Зберегти',
    cancel: 'Скасувати',
    delete: 'Видалити',
    create: 'Створити',
    edit: 'Редагувати',
    close: 'Закрити',
    open: 'Відкрити',
    export: 'Експорт',
    import: 'Імпорт',
    recalc: 'Перерахувати',
    review: 'Переглянути',
    mark_paid: 'Сплачено',
    clear: 'Очистити',
    configure: 'Налаштувати',
    connect: 'Підключити',
    reconnect: 'Перепідключити',
    logs: 'Журнал',
    add_payment: 'Додати платіж',
    duplicate: 'Дублювати',
    archive: 'В архів',
    back: 'Назад',
    search: 'Пошук',
  },
  status: {
    paid: 'Сплачено',
    due_soon: 'Скоро',
    overdue: 'Прострочено',
    draft: 'Чернетка',
    archived: 'В архіві',
    active: 'Активна',
    ok: 'У нормі',
    gap: 'Розрив',
    // License funnel commitment
    debt: 'Борг',
    purchased: 'Куплено',
    expired: 'Прострочено',
  },
  types: { Inflow: 'Надходження', Outflow: 'Витрата', Salary: 'Зарплата', Transfer: 'Переказ' },
  filters: {
    period: 'Період', account: 'Рахунок', company: 'Компанія', status: 'Статус',
    category: 'Категорія', project: 'Проєкт', type: 'Тип', counterparty: 'Контрагент',
  },
  table: {
    date: 'Дата', type: 'Тип', counterparty: 'Контрагент', account: 'Рахунок',
    category: 'Категорія', project: 'Проєкт', description: 'Опис', status: 'Статус',
    amount: 'Сума', balance: 'Баланс', total: 'Усього', period: 'Період',
    open: 'Початок', income: '+ Надходження', expense: '− Витрата', close: 'Залишок',
    week: 'Тиждень', label: 'Призначення', direction: 'Напрям',
  },
  dash: {
    balances_title: 'Баланси за рахунками',
    total_balance: 'Загальний баланс',
    in_base: 'у базовій валюті',
    open_commit: 'Відкриті зобов’язання',
    cashflow_mtd: 'Грошовий потік',
    cashflow_meta: 'за травень',
    next_date: 'Найближча подія',
    forecast_4w: 'Прогноз на 4 тижні',
    alerts: 'Сповіщення',
    quick: 'Швидкі дії',
    refreshed_ago: (m: number) => `Оновлено ${m} хв тому`,
    accounts_count: (n: number) =>
      `${n} ${n === 1 ? 'рахунок' : n >= 2 && n <= 4 ? 'рахунки' : 'рахунків'}`,
    invoices_outstanding: (n: number) => `${n} рахунків очікують`,
    vs_prev_month: 'проти −18к у квітні',
  },
  acc: {
    title: 'Рахунки',
    overview: 'Огляд',
    last_change: 'Зміна за тиждень',
    last4: 'Останні 4',
    no_activity: 'Без активності',
    open_tx: 'Відкрити операції',
    transfer: 'Переказ',
    by_company: 'За організаціями',
  },
} as const
